import { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtCFA } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, CalendarClock } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import ImageUpload from "@/components/ImageUpload";
import Thumb from "@/components/Thumb";

const TYPES = ["CrossFit", "Cardio", "Musculation", "Yoga", "Boxe", "Danse"];
const EMPTY = { nom: "", type: "Musculation", coach: "", horaire: "", duree: 60, capacite: 20, prix: 0, image_path: null };

export default function Seances() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => api.get("/seances").then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement")), []);
  useEffect(() => { refresh(); }, [refresh]);

  const openForm = (s) => {
    setEditing(s || null);
    setForm(s ? { nom: s.nom, type: s.type, coach: s.coach, horaire: s.horaire, duree: s.duree, capacite: s.capacite, prix: s.prix, image_path: s.image_path || null } : EMPTY);
    setDialog(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, duree: Number(form.duree), capacite: Number(form.capacite), prix: Number(form.prix) };
      if (editing) {
        await api.put(`/seances/${editing.id}`, payload);
        toast.success("Séance modifiée");
      } else {
        await api.post("/seances", payload);
        toast.success("Séance ajoutée");
      }
      setDialog(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/seances/${toDelete.id}`);
      toast.success("Séance supprimée");
      setToDelete(null);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="seances-loading">Chargement…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="seances-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Séances</h1>
          <p className="text-sm text-muted-foreground mt-1">Catalogue des séances proposées par la salle</p>
        </div>
        {can("seances_gerer") && (
        <Button data-testid="add-seance-button" onClick={() => openForm(null)} className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
          <Plus className="w-4 h-4 mr-2" /> Ajouter une séance
        </Button>
        )}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground">Nom</TableHead>
                <TableHead className="text-muted-foreground">Type</TableHead>
                <TableHead className="text-muted-foreground">Coach</TableHead>
                <TableHead className="text-muted-foreground">Horaire</TableHead>
                <TableHead className="text-muted-foreground">Durée</TableHead>
                <TableHead className="text-muted-foreground">Capacité</TableHead>
                <TableHead className="text-muted-foreground text-right">Prix</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8" data-testid="seances-empty">Aucune séance</TableCell>
                </TableRow>
              )}
              {items.map((s) => (
                <TableRow key={s.id} className="border-border" data-testid={`seance-row-${s.id}`}>
                  <TableCell className="font-medium text-foreground">
                    <div className="flex items-center gap-3">
                      <Thumb path={s.image_path} alt={s.nom} icon={CalendarClock} testid={`seance-photo-${s.id}`} />
                      {s.nom}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="border-blue-500/40 text-blue-600 dark:text-blue-400">{s.type}</Badge></TableCell>
                  <TableCell className="text-foreground/80">{s.coach}</TableCell>
                  <TableCell className="text-foreground/80">{s.horaire}</TableCell>
                  <TableCell className="text-foreground/80">{s.duree} min</TableCell>
                  <TableCell className="text-foreground/80">{s.capacite}</TableCell>
                  <TableCell className="text-right text-emerald-600 dark:text-emerald-400 font-semibold">{fmtCFA(s.prix)}</TableCell>
                  <TableCell className="text-right">
                    {can("seances_gerer") && (
                    <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400" data-testid={`edit-seance-${s.id}`} onClick={() => openForm(s)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    )}
                    {can("seances_gerer") && (
                      <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-seance-${s.id}`} onClick={() => setToDelete(s)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle data-testid="seance-dialog-title">{editing ? "Modifier la séance" : "Ajouter une séance"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <ImageUpload value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} testid="seance-image-upload" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="text-foreground/80">Nom</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="seance-nom-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="seance-type-select" className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Coach</Label>
              <Input value={form.coach} onChange={(e) => setForm({ ...form, coach: e.target.value })} data-testid="seance-coach-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Horaire</Label>
              <Input type="time" value={form.horaire} onChange={(e) => setForm({ ...form, horaire: e.target.value })} data-testid="seance-horaire-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Durée (min)</Label>
              <Input type="number" min="1" value={form.duree} onChange={(e) => setForm({ ...form, duree: e.target.value })} data-testid="seance-duree-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Capacité</Label>
              <Input type="number" min="1" value={form.capacite} onChange={(e) => setForm({ ...form, capacite: e.target.value })} data-testid="seance-capacite-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Prix (FCFA)</Label>
              <Input type="number" min="0" value={form.prix} onChange={(e) => setForm({ ...form, prix: e.target.value })} data-testid="seance-prix-input" className="bg-background border-border text-foreground" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-border text-foreground/80">Annuler</Button>
            <Button onClick={save} disabled={busy || !form.nom} data-testid="save-seance-button" className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={!!toDelete} onOpenChange={() => setToDelete(null)} onConfirm={remove} title={toDelete?.nom} busy={busy} />
    </div>
  );
}
